import React from 'react';
import { useWithExtraFieldsQuery } from '../../../__generated__/graphql';
import {
  ExtraFieldsValueToRender,
  ExtraFieldsViewAreaContent,
  Loading,
} from './ExtraFieldsViewAreaContent';
import { useObjectExtraFields } from '../useObjectExtraFields';
import {
  ExtraFieldsFormId,
  ExtraFieldsSupportedObject,
  ExtraFieldContextParam,
} from '../types';
import { ExtraFieldsError } from '../ExtraFieldsError';
import { mapToExtraFieldInternalValue } from '../extraFieldInternalValue';

type Props = {
  objectApiName: ExtraFieldsSupportedObject | string;
  recordId: string;
  formId: ExtraFieldsFormId | string;
  columnsCount?: number;
  renderItems?: (items: ExtraFieldsValueToRender[]) => React.ReactNode;
  contextParams?: ExtraFieldContextParam[];
  hideRecordType?: boolean;
};

export const ExtraFieldsViewArea: React.FC<React.PropsWithChildren<Props>> = ({
  objectApiName,
  formId,
  recordId,
  columnsCount = 1,
  renderItems,
  contextParams,
  hideRecordType = false,
}) => {
  const { getFormFields, loading, error } = useObjectExtraFields(objectApiName);
  const formFields = getFormFields(formId);
  const skipValueLoading = formFields.length === 0;
  const {
    data,
    loading: valuesLoading,
    error: valuesError,
  } = useWithExtraFieldsQuery({
    variables: { recordId, objectApiName },
    skip: skipValueLoading,
  });

  if (loading || valuesLoading) return <Loading columnsCount={columnsCount} />;
  if (error || valuesError)
    return (
      <ExtraFieldsError
        error={error?.message ?? valuesError?.message ?? 'Unknown error'}
      />
    );

  if (!formFields.length) return null;
  if (!data) return null;

  const { extraFields, recordType } = data.extraFields.withExtraFields;

  return (
    <ExtraFieldsViewAreaContent
      values={extraFields.map(mapToExtraFieldInternalValue)}
      formFields={formFields}
      recordType={recordType}
      columnsCount={columnsCount}
      renderItems={renderItems}
      contextParams={contextParams}
      hideRecordType={hideRecordType}
    />
  );
};
